// frontend/src/components/UrbanIA/UrbanIA.js
// UrbanIA - Asistente Inteligente UrbanQ v2.0
// Con sugerencias interactivas, navegación integrada y soporte multi-usuario
// v1.9.5.6 - Con selector de modelos AI
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import UrbanIASidebar from './UrbanIASidebar';
import { API_ENDPOINTS } from '../../config/api';
import './UrbanIA.css';

// ─────────────────────────────────────────────
// CONFIGURACIÓN
// ─────────────────────────────────────────────
const API_BASE = process.env.REACT_APP_API_URL || '/api';

// ─────────────────────────────────────────────
// CONFIGURACIÓN DE MODELOS AI
// ─────────────────────────────────────────────
const AI_MODELS = [
  { id: 'urbanq-ia', name: 'UrbanQ IA', size: '8b', prodEnabled: true, devEnabled: true },
  { id: 'deepseek-r1:1.5b', name: 'DeepSeek R1', size: '1.5b', prodEnabled: false, devEnabled: false },
  { id: 'deepseek-r1:7b', name: 'DeepSeek R1', size: '7b', prodEnabled: false, devEnabled: true },
  { id: 'qwen2.5:3b', name: 'Qwen 2.5', size: '3b', prodEnabled: true, devEnabled: true },
  { id: 'llama3.2:3b', name: 'Llama 3.2', size: '3b', prodEnabled: false, devEnabled: false },
];

// Detectar si estamos en producción (nextunicodecorp.com)
const isProduction = (process.env.REACT_APP_API_URL || '').includes('nextunicodecorp');
const DEFAULT_MODEL = 'urbanq-ia';

// Constantes de historial
const CONTEXT_WARNING_THRESHOLD = 16; // mensajes para warning amarillo
const CONTEXT_LIMIT_THRESHOLD = 20;   // mensajes para warning rojo
const AUTO_TITLE_LENGTH = 40;         // caracteres max para titulo auto-generado

// Sugerencias mientras procesa (por rol)
const LOADING_SUGGESTIONS = {
  SuperAdmin: [
    { text: '📊 Ver estadísticas', action: 'navigate', url: 'stats' },
    { text: '🏢 Gestionar condominios', action: 'navigate', url: 'condominiums' },
    { text: '👥 Ver administradores', action: 'navigate', url: 'admins' },
    { text: '📋 Reportería', action: 'navigate', url: 'reports' },
  ],
  Admin: [
    { text: '💰 Ver pagos', action: 'navigate', url: '/admin/payments' },
    { text: '📢 Ver avisos', action: 'navigate', url: '/admin/notices' },
    { text: '👥 Residentes', action: 'navigate', url: '/admin/users' },
  ],
  User: [
    { text: '💳 Mi deuda', action: 'chat', message: '¿Cuánto debo?' },
    { text: '📢 Avisos', action: 'navigate', url: '/dashboard/notices' },
    { text: '📋 Mis pagos', action: 'chat', message: 'Dame mi historial de pagos' },
  ],
};

// ═══════════════════════════════════════════════════════════════
// SUGERENCIAS CONTEXTUALES PARA USUARIOS
// ═══════════════════════════════════════════════════════════════
const SUGGESTIONS = {
  INITIAL: [
    { text: "💰 ¿Cuánto debo?", icon: "💰", action: 'chat', message: '¿Cuánto debo?' },
    { text: "📋 Ver mis pagos", icon: "📋", action: 'chat', message: 'Dame mi historial de pagos' },
    { text: "📢 Avisos recientes", icon: "📢", action: 'chat', message: '¿Qué avisos hay?' },
    { text: "❓ ¿Qué puedes hacer?", icon: "❓", action: 'chat', message: '¿Qué puedes hacer?' }
  ],
  DEUDA: [
    { text: "📅 ¿Cuándo vence?", icon: "📅", action: 'chat', message: '¿Cuándo vence mi próxima alícuota?' },
    { text: "💳 ¿Cómo pago?", icon: "💳", action: 'chat', message: '¿Cómo puedo pagar?' },
    { text: "📊 Historial de pagos", icon: "📊", action: 'chat', message: 'Dame mi historial de pagos' },
    { text: "🧾 Ver facturas", icon: "🧾", action: 'navigate', url: '/dashboard/invoices' }
  ],
  PAGOS: [
    { text: "💰 Mi deuda actual", icon: "💰", action: 'chat', message: '¿Cuánto debo?' },
    { text: "📅 Próximos vencimientos", icon: "📅", action: 'chat', message: '¿Cuándo vence mi próxima alícuota?' },
    { text: "🧾 Mis facturas", icon: "🧾", action: 'navigate', url: '/dashboard/invoices' },
    { text: "📢 Ver avisos", icon: "📢", action: 'chat', message: '¿Qué avisos hay?' }
  ],
  AVISOS: [
    { text: "📢 ¿Avisos urgentes?", icon: "📢", action: 'chat', message: '¿Hay avisos urgentes?' },
    { text: "💰 Mi deuda", icon: "💰", action: 'chat', message: '¿Cuánto debo?' },
    { text: "📞 Contactar admin", icon: "📞", action: 'chat', message: '¿Cómo contacto al administrador?' },
    { text: "🏠 Info condominio", icon: "🏠", action: 'chat', message: 'Dame información del condominio' }
  ],
  GENERAL: [
    { text: "💰 Mi deuda", icon: "💰", action: 'chat', message: '¿Cuánto debo?' },
    { text: "📋 Mis pagos", icon: "📋", action: 'chat', message: 'Dame mi historial de pagos' },
    { text: "📢 Avisos", icon: "📢", action: 'chat', message: '¿Qué avisos hay?' },
    { text: "❓ Ayuda", icon: "❓", action: 'chat', message: '¿Qué puedes hacer?' }
  ]
};

// Genera sugerencias contextuales basadas en la respuesta del bot
const getContextualSuggestions = (botResponse, role) => {
  const text = (botResponse || '').toLowerCase();
  const suggestions = [];

  if (role === 'Admin') {
    if (text.includes('pago') || text.includes('deuda') || text.includes('pendiente') || text.includes('debe')) {
      suggestions.push({ text: '💰 Ver pagos', action: 'navigate', url: '/admin/payments' });
    }
    if (text.includes('aviso') || text.includes('comunicado') || text.includes('publicado')) {
      suggestions.push({ text: '📢 Ver avisos', action: 'navigate', url: '/admin/notices' });
    }
    if (text.includes('residente') || text.includes('usuario') || text.includes('inquilino')) {
      suggestions.push({ text: '👥 Ver residentes', action: 'navigate', url: '/admin/users' });
    }
    if (text.includes('ingreso') || text.includes('mes') || text.includes('cobr')) {
      suggestions.push({ text: '📊 Ver pagos', action: 'navigate', url: '/admin/payments' });
    }
  } else if (role === 'User') {
    if (text.includes('deuda') || text.includes('debes') || text.includes('pendiente') || text.includes('pagar')) {
      suggestions.push({ text: '💳 Pagar ahora', action: 'navigate', url: '/dashboard/payments' });
    }
    if (text.includes('factura') || text.includes('recibo') || text.includes('comprobante')) {
      suggestions.push({ text: '📋 Mis facturas', action: 'navigate', url: '/dashboard/invoices' });
    }
    if (text.includes('aviso') || text.includes('comunicado') || text.includes('anuncio')) {
      suggestions.push({ text: '📢 Ver avisos', action: 'navigate', url: '/dashboard/notices' });
    }
    if (text.includes('vencimiento') || text.includes('fecha') || text.includes('próximo')) {
      suggestions.push({ text: '📅 Ver calendario', action: 'chat', message: '¿Cuándo vence mi próxima alícuota?' });
    }
  }

  // Si no hay suficientes, usar defaults
  if (suggestions.length < 2) {
    return LOADING_SUGGESTIONS[role] || LOADING_SUGGESTIONS.User;
  }

  // Eliminar duplicados
  const unique = suggestions.filter((s, i, arr) =>
    arr.findIndex(x => x.url === s.url || x.message === s.message) === i
  );
  return unique.slice(0, 3);
};

// ─────────────────────────────────────────────
// COMPONENTE PRINCIPAL
// ─────────────────────────────────────────────
const UrbanIA = ({ user, onNavigate }) => {
  const navigate = useNavigate();

  // Estado del chat
  const [isOpen, setIsOpen]           = useState(false);
  const [isExpanded, setIsExpanded]   = useState(false);
  const [isCollapsing, setIsCollapsing] = useState(false);
  const [selectedModel, setSelectedModel] = useState(DEFAULT_MODEL);
  const [showModelDropdown, setShowModelDropdown] = useState(false);
  // Clave de sessionStorage por usuario (se borra al cerrar navegador/sesión)
  const storageKey = `urbanIA_chat_${user?.id || 'guest'}`;

  const [messages, setMessages]       = useState(() => {
    try {
      const saved = sessionStorage.getItem(storageKey);
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });
  const [input, setInput]             = useState('');
  const [isLoading, setIsLoading]     = useState(false);
  const [hasGreeted, setHasGreeted]   = useState(() => {
    try {
      const saved = sessionStorage.getItem(storageKey);
      const msgs = saved ? JSON.parse(saved) : [];
      return msgs.length > 0; // Si hay mensajes guardados, ya saludó
    } catch { return false; }
  });

  // Modal de navegación
  const [navModal, setNavModal]       = useState(null); // { url, text }

  // Sugerencias mientras carga
  const [loadingSuggestions, setLoadingSuggestions] = useState([]);

  // ── HISTORIAL (Persistencia de chats) ──
  const [conversations, setConversations] = useState([]);
  const [folders, setFolders] = useState([]);
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [showSidebar, setShowSidebar] = useState(false); // overlay mode
  const [contextWarning, setContextWarning] = useState(null); // 'yellow' | 'red' | null
  const [messageCount, setMessageCount] = useState(0);

  // Refs
  const messagesEndRef  = useRef(null);
  const inputRef        = useRef(null);
  const historyRef      = useRef([]); // Historial para el backend (sin renderizar)

  // Rol del usuario
  const role = user?.role || 'User';

  // ── Persistir mensajes en sessionStorage ────
  useEffect(() => {
    if (messages.length > 0) {
      try { sessionStorage.setItem(storageKey, JSON.stringify(messages)); }
      catch { /* ignorar errores de storage */ }
    }
  }, [messages, storageKey]);

  // ── Scroll automático ─────────────────────
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  // ── Saludo inicial al abrir ───────────────
  useEffect(() => {
    if (!isOpen || hasGreeted) return;
    setHasGreeted(true);

    const greetings = {
      SuperAdmin: `¡Hola, ${user?.name}! 👋 Soy **UrbanIA**, tu asistente con acceso completo al sistema UrbanQ.\n\nPuedo mostrarte en tiempo real: cuántos condominios están activos, quiénes deben, cuánto entró este mes, qué suscripciones vencen pronto y mucho más. Todo directo de la base de datos. ¿Qué quieres revisar?`,
      Admin: `¡Hola, ${user?.name}! 👋 Soy **UrbanIA**, tu asistente para gestionar tu condominio.\n\nTengo acceso a tus datos en tiempo real: pagos pendientes, quiénes deben, ingresos del mes, avisos publicados y el estado de tus residentes. ¿Qué necesitas?`,
      User: `¡Hola, ${user?.name}! 👋 Soy **UrbanIA**, tu asistente en el condominio.\n\nPuedo decirte cuánto debes, cuándo vence tu próximo pago, ver tus facturas y los avisos del condominio. ¿En qué te ayudo?`,
    };

    const suggestions = {
      SuperAdmin: [
        { text: '📊 Resumen del sistema', action: 'chat', message: 'Dame un resumen del sistema de hoy' },
        { text: '💰 Deuda total', action: 'chat', message: '¿Cuál es la deuda total del sistema?' },
        { text: '🏢 Condominios activos', action: 'chat', message: '¿Cuántos condominios activos hay?' },
        { text: '⏰ Suscripciones por vencer', action: 'chat', message: '¿Qué suscripciones vencen esta semana?' },
        { text: '📈 Ingresos del mes', action: 'chat', message: '¿Cuánto ingresó este mes?' },
        { text: '👥 Total usuarios', action: 'chat', message: '¿Cuántos usuarios hay en el sistema?' },
      ],
      Admin: [
        { text: '💰 Pagos pendientes', action: 'chat', message: '¿Quiénes deben alícuotas?' },
        { text: '📈 Ingresos del mes', action: 'chat', message: '¿Cuánto ingresó este mes?' },
        { text: '📢 Últimos avisos', action: 'chat', message: '¿Qué avisos están publicados?' },
        { text: '👥 Total residentes', action: 'chat', message: '¿Cuántos residentes tengo?' },
      ],
      User: [
        { text: '💳 ¿Cuánto debo?', action: 'chat', message: '¿Cuánto debo?' },
        { text: '📅 Próximo vencimiento', action: 'chat', message: '¿Cuándo vence mi próxima alícuota?' },
        { text: '📋 Historial de pagos', action: 'chat', message: 'Dame mi historial de pagos' },
        { text: '📢 Avisos del condominio', action: 'chat', message: '¿Qué avisos hay?' },
      ],
    };

    addBotMessage(greetings[role] || greetings.User, suggestions[role] || []);
  }, [isOpen, hasGreeted, role, user?.name]);

  // ── Agregar mensaje del bot ───────────────
  const addBotMessage = useCallback((text, suggestions = [], thinking = '') => {
    const msg = {
      id: Date.now(),
      type: 'bot',
      text,
      suggestions,
      thinking,
      time: new Date().toLocaleTimeString('es-EC', { hour: '2-digit', minute: '2-digit' }),
    };
    setMessages(prev => [...prev, msg]);
    return msg;
  }, []);

  // ── HISTORIAL: Fetch conversaciones ──────────
  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    };
  }, []);

  const fetchConversations = useCallback(async () => {
    try {
      const res = await fetch(API_ENDPOINTS.urbaniaConversations, { headers: getAuthHeaders() });
      const data = await res.json();
      if (data.success) setConversations(data.data || []);
    } catch (err) {
      console.error('[UrbanIA] Error fetching conversations:', err);
    }
  }, [getAuthHeaders]);

  const fetchFolders = useCallback(async () => {
    try {
      const res = await fetch(API_ENDPOINTS.urbaniaFolders, { headers: getAuthHeaders() });
      const data = await res.json();
      if (data.success) setFolders(data.data || []);
    } catch (err) {
      console.error('[UrbanIA] Error fetching folders:', err);
    }
  }, [getAuthHeaders]);

  // Cargar historial al abrir
  useEffect(() => {
    if (isOpen && user?.id) {
      fetchConversations();
      fetchFolders();
    }
  }, [isOpen, user?.id, fetchConversations, fetchFolders]);

  // ── HISTORIAL: Handlers ───────────────────
  const handleNewChat = useCallback(() => {
    setActiveConversationId(null);
    setMessages([]);
    setMessageCount(0);
    setContextWarning(null);
    historyRef.current = [];
    setHasGreeted(false);
    setShowSidebar(false);
    try { sessionStorage.removeItem(storageKey); } catch {}
    setTimeout(() => setHasGreeted(false), 100);
  }, [storageKey]);

  const handleSelectConversation = useCallback(async (convId) => {
    try {
      const res = await fetch(API_ENDPOINTS.urbaniaMessages(convId), { headers: getAuthHeaders() });
      const data = await res.json();
      if (data.success) {
        // Convertir mensajes de BD al formato del chat
        const chatMessages = (data.data || []).map((m, i) => ({
          id: m.id || Date.now() + i,
          type: m.role === 'assistant' ? 'bot' : 'user',
          text: m.content,
          suggestions: m.suggestions ? (typeof m.suggestions === 'string' ? JSON.parse(m.suggestions) : m.suggestions) : [],
          thinking: m.thinking || '',
          time: new Date(m.created_at).toLocaleTimeString('es-EC', { hour: '2-digit', minute: '2-digit' }),
        }));
        setMessages(chatMessages);
        setActiveConversationId(convId);
        setHasGreeted(true);
        // Actualizar historial para backend
        historyRef.current = (data.data || []).slice(-10).map(m => ({
          role: m.role,
          content: m.content,
        }));
        // Verificar limite de contexto
        const count = data.data?.length || 0;
        setMessageCount(count);
        checkContextLimit(count);
        setShowSidebar(false);
      }
    } catch (err) {
      console.error('[UrbanIA] Error loading conversation:', err);
    }
  }, [getAuthHeaders]);

  const handleDeleteConversation = useCallback(async (convId) => {
    try {
      const res = await fetch(API_ENDPOINTS.urbaniaConversationDelete(convId), {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      const data = await res.json();
      if (data.success) {
        // Si eliminamos la conversacion activa, limpiar
        if (convId === activeConversationId) {
          handleNewChat();
        }
        fetchConversations();
      }
    } catch (err) {
      console.error('[UrbanIA] Error deleting conversation:', err);
    }
  }, [activeConversationId, getAuthHeaders, handleNewChat, fetchConversations]);

  const handleRenameConversation = useCallback(async (convId, newTitle) => {
    try {
      await fetch(API_ENDPOINTS.urbaniaConversationTitle(convId), {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ title: newTitle }),
      });
      fetchConversations();
    } catch (err) {
      console.error('[UrbanIA] Error renaming conversation:', err);
    }
  }, [getAuthHeaders, fetchConversations]);

  const handleCreateFolder = useCallback(async (name) => {
    try {
      const res = await fetch(API_ENDPOINTS.urbaniaFolders, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ name }),
      });
      const data = await res.json();
      if (data.success) fetchFolders();
    } catch (err) {
      console.error('[UrbanIA] Error creating folder:', err);
    }
  }, [getAuthHeaders, fetchFolders]);

  const handleDeleteFolder = useCallback(async (folderId) => {
    try {
      await fetch(API_ENDPOINTS.urbaniaFolderDelete(folderId), {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      fetchFolders();
      fetchConversations(); // Los chats de ese folder ahora no tienen folder
    } catch (err) {
      console.error('[UrbanIA] Error deleting folder:', err);
    }
  }, [getAuthHeaders, fetchFolders, fetchConversations]);

  const handleMoveToFolder = useCallback(async (convId, folderId) => {
    try {
      await fetch(API_ENDPOINTS.urbaniaConversationFolder(convId), {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ folderId }),
      });
      fetchConversations();
    } catch (err) {
      console.error('[UrbanIA] Error moving conversation:', err);
    }
  }, [getAuthHeaders, fetchConversations]);

  // ── Verificar limite de contexto ──────────
  const checkContextLimit = useCallback((count) => {
    if (count >= CONTEXT_LIMIT_THRESHOLD) {
      setContextWarning('red');
    } else if (count >= CONTEXT_WARNING_THRESHOLD) {
      setContextWarning('yellow');
    } else {
      setContextWarning(null);
    }
  }, []);

  // ── Crear conversacion automaticamente ────
  const ensureConversation = useCallback(async (firstMessage) => {
    if (activeConversationId) return activeConversationId;

    try {
      // Auto-titulo con las primeras palabras del mensaje
      const autoTitle = firstMessage.length > AUTO_TITLE_LENGTH
        ? firstMessage.substring(0, AUTO_TITLE_LENGTH) + '...'
        : firstMessage;

      const res = await fetch(API_ENDPOINTS.urbaniaConversations, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ title: autoTitle, model: selectedModel }),
      });
      const data = await res.json();

      if (data.success && data.data?.id) {
        setActiveConversationId(data.data.id);
        fetchConversations();
        return data.data.id;
      } else if (data.limitReached) {
        // Limite alcanzado - mostrar en chat
        addBotMessage(
          '⚠️ Has alcanzado el limite de 10 conversaciones. Elimina una conversacion antigua para poder crear una nueva.',
          [{ text: '📋 Ver historial', action: 'chat', message: '' }]
        );
        setShowSidebar(true);
        return null;
      }
    } catch (err) {
      console.error('[UrbanIA] Error creating conversation:', err);
    }
    return null;
  }, [activeConversationId, selectedModel, getAuthHeaders, fetchConversations, addBotMessage]);

  // ── Agregar mensaje del usuario ───────────
  const addUserMessage = useCallback((text) => {
    const msg = {
      id: Date.now(),
      type: 'user',
      text,
      time: new Date().toLocaleTimeString('es-EC', { hour: '2-digit', minute: '2-digit' }),
    };
    setMessages(prev => [...prev, msg]);
    return msg;
  }, []);

  // ── Enviar mensaje ─────────────────────────
  const sendMessage = useCallback(async (text) => {
    if (!text?.trim() || isLoading) return;

    const msgText = text.trim();
    addUserMessage(msgText);
    setInput('');
    setIsLoading(true);

    // Sugerencias mientras carga
    const roleSuggestions = LOADING_SUGGESTIONS[role] || LOADING_SUGGESTIONS.User;
    setLoadingSuggestions(roleSuggestions);

    // Actualizar historial
    historyRef.current = [
      ...historyRef.current.slice(-10),
      { role: 'user', content: msgText }
    ];

    try {
      // Asegurar que existe una conversacion (crear si no hay)
      const convId = await ensureConversation(msgText);
      if (convId === null && !activeConversationId) {
        // Limite alcanzado, no enviar
        setIsLoading(false);
        setLoadingSuggestions([]);
        return;
      }

      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const response = await fetch(`${API_BASE}/agent/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          message: msgText,
          history: historyRef.current.slice(-6),
          model: selectedModel,
          conversationId: convId || activeConversationId,
        }),
      });

      const data = await response.json();

      if (data.success) {
        // Actualizar historial con respuesta del bot
        historyRef.current = [
          ...historyRef.current,
          { role: 'assistant', content: data.response }
        ];

        // Usar sugerencias del backend, o generar contextuales locales
        const suggestions = (data.suggestions && data.suggestions.length > 0)
          ? data.suggestions
          : getContextualSuggestions(data.response, role);

        addBotMessage(data.response, suggestions, data.thinking || '');

        // Actualizar messageCount y verificar limite de contexto
        if (data.messageCount) {
          setMessageCount(data.messageCount);
          checkContextLimit(data.messageCount);
        }

        // Refrescar lista de conversaciones (para actualizar updated_at)
        fetchConversations();
      } else {
        addBotMessage(
          data.response || 'Ocurrio un error. Por favor intenta de nuevo.',
          getContextualSuggestions(data.response, role)
        );
      }
    } catch (error) {
      console.error('[UrbanIA] Error:', error);
      addBotMessage(
        'No pude conectarme al servidor. Verifica tu conexion e intenta de nuevo.',
        LOADING_SUGGESTIONS[role] || LOADING_SUGGESTIONS.User
      );
    } finally {
      setIsLoading(false);
      setLoadingSuggestions([]);
      inputRef.current?.focus();
    }
  }, [isLoading, role, addUserMessage, addBotMessage, selectedModel, activeConversationId, ensureConversation, checkContextLimit, fetchConversations]);

  // ── Manejar sugerencia clickeada ──────────
  const handleSuggestion = useCallback((suggestion) => {
    if (suggestion.action === 'chat') {
      sendMessage(suggestion.message || suggestion.text);
    } else if (suggestion.action === 'navigate') {
      // Mostrar modal de confirmación antes de navegar
      setNavModal({ url: suggestion.url, text: suggestion.text });
    } else if (suggestion.action === 'open_pdf') {
      window.open(suggestion.url, '_blank');
    }
  }, [sendMessage]);

  // ── Confirmar navegación ──────────────────
  const confirmNavigation = useCallback(() => {
    if (!navModal) return;
    const url = navModal.url;
    setNavModal(null);

    // Navegar a la sección del dashboard
    if (onNavigate) {
      onNavigate(url);
    } else {
      navigate(url);
    }

    // Minimizar chat al navegar
    setIsExpanded(false);
  }, [navModal, navigate, onNavigate]);

  // ── Enter para enviar ─────────────────────
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  // ── Toggle expand/collapse con animación 3D ──
  const handleToggleExpand = () => {
    if (isExpanded) {
      // Iniciar animación de collapse
      setIsCollapsing(true);
      setTimeout(() => {
        setIsExpanded(false);
        setIsCollapsing(false);
      }, 300); // Duración de collapse3D
    } else {
      setIsExpanded(true);
    }
  };

  // ── Limpiar conversación ──────────────────
  const clearChat = () => {
    setMessages([]);
    historyRef.current = [];
    setHasGreeted(false);
    setActiveConversationId(null);
    setMessageCount(0);
    setContextWarning(null);
    try { sessionStorage.removeItem(storageKey); } catch {}
    setTimeout(() => setHasGreeted(false), 100);
  };

  // ─────────────────────────────────────────
  // HELPERS
  // ─────────────────────────────────────────
  function getDefaultSuggestions(role) {
    return {
      SuperAdmin: [
        { text: '📊 Ver estadísticas', action: 'navigate', url: '/superadmin/stats' },
        { text: '🔄 Reintentar', action: 'chat', message: '¿Cuántos condominios activos hay?' },
      ],
      Admin: [
        { text: '💰 Pagos pendientes', action: 'chat', message: '¿Quiénes deben alícuotas?' },
        { text: '🔄 Reintentar', action: 'chat', message: 'Dame un resumen del condominio' },
      ],
      User: [
        { text: '💳 Mi deuda', action: 'chat', message: '¿Cuánto debo?' },
        { text: '🔄 Reintentar', action: 'chat', message: 'Dame mis datos' },
      ],
    }[role] || [];
  }

  // Nota: Ahora usamos ReactMarkdown para renderizar el texto

  // ─────────────────────────────────────────
  // NO MOSTRAR SI NO HAY USUARIO CON ROL VÁLIDO
  // ─────────────────────────────────────────
  if (!user || !['SuperAdmin', 'Admin', 'User'].includes(role)) return null;

  // ─────────────────────────────────────────
  // RENDERIZADO
  // ─────────────────────────────────────────
  return (
    <>
      {/* ── VENTANA DEL CHAT ── */}
      {isOpen && (
        <div className={`urbania-window ${isExpanded ? 'urbania-expanded' : ''} ${isCollapsing ? 'urbania-collapsing' : ''}`}>

          {/* Header */}
          <div className="urbania-header">
            <div className="urbania-header-info">
              <div className="urbania-avatar">
                <span>🤖</span>
                <span className="urbania-status-dot" />
              </div>
              <div>
                <h4 className="urbania-title">UrbanIA</h4>
                {/* Selector de Modelo */}
                <div className="urbania-model-selector">
                  <button
                    className="urbania-model-btn"
                    onClick={() => setShowModelDropdown(!showModelDropdown)}
                    title="Seleccionar modelo de IA"
                  >
                    <span className="urbania-model-dot"></span>
                    {AI_MODELS.find(m => m.id === selectedModel)?.name || 'DeepSeek R1'} · {AI_MODELS.find(m => m.id === selectedModel)?.size || '1.5b'}
                    <span className="urbania-model-arrow">{showModelDropdown ? '▲' : '▼'}</span>
                  </button>
                  {showModelDropdown && (
                    <div className="urbania-model-dropdown">
                      {AI_MODELS
                        .filter(m => isProduction ? m.prodEnabled : m.devEnabled)
                        .map(model => {
                        const isSelected = model.id === selectedModel;
                        return (
                          <button
                            key={model.id}
                            className={`urbania-model-option ${isSelected ? 'selected' : ''}`}
                            onClick={() => {
                              setSelectedModel(model.id);
                              setShowModelDropdown(false);
                            }}
                            title={`Usar ${model.name} ${model.size}`}
                          >
                            <span className="urbania-model-name">{model.name} · {model.size}</span>
                            {isSelected && <span className="urbania-model-check">✓</span>}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="urbania-header-actions">
              {/* Boton historial (solo modo normal, no expandido) */}
              {!isExpanded && (
                <button
                  className="urbania-history-btn"
                  onClick={() => setShowSidebar(!showSidebar)}
                  title="Historial de chats"
                >
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                    <path d="M13 3c-4.97 0-9 4.03-9 9H1l3.89 3.89.07.14L9 12H6c0-3.87 3.13-7 7-7s7 3.13 7 7-3.13 7-7 7c-1.93 0-3.68-.79-4.94-2.06l-1.42 1.42C8.27 19.99 10.51 21 13 21c4.97 0 9-4.03 9-9s-4.03-9-9-9zm-1 5v5l4.28 2.54.72-1.21-3.5-2.08V8H12z"/>
                  </svg>
                </button>
              )}
              <button
                className="urbania-btn-icon"
                onClick={handleToggleExpand}
                title={isExpanded ? 'Reducir' : 'Expandir'}
              >
                {isExpanded ? '⊡' : '⊞'}
              </button>
              <button
                className="urbania-btn-icon"
                onClick={handleNewChat}
                title="Nuevo chat"
              >
                +
              </button>
              <button
                className="urbania-btn-icon urbania-btn-close"
                onClick={() => setIsOpen(false)}
                title="Cerrar"
              >
                ✕
              </button>
            </div>
          </div>

          {/* ── Layout con sidebar (expandido) o solo chat (normal) ── */}
          {isExpanded ? (
            <div className="urbania-expanded-layout">
              {/* Sidebar siempre visible en modo expandido */}
              <UrbanIASidebar
                conversations={conversations}
                folders={folders}
                activeConversationId={activeConversationId}
                onSelectConversation={handleSelectConversation}
                onNewChat={handleNewChat}
                onDeleteConversation={handleDeleteConversation}
                onRenameConversation={handleRenameConversation}
                onCreateFolder={handleCreateFolder}
                onDeleteFolder={handleDeleteFolder}
                onMoveToFolder={handleMoveToFolder}
                isOverlay={false}
              />

              {/* Chat principal */}
              <div className="urbania-main-chat">
                {/* Mensajes */}
                <div className="urbania-messages">
                  {messages.map(msg => (
                    <div key={msg.id} className={`urbania-msg urbania-msg-${msg.type}`}>
                      <div className="urbania-bubble">
                        <div className="urbania-bubble-text">
                          {msg.type === 'bot' ? (
                            <div className="urbania-markdown">
                              <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.text}</ReactMarkdown>
                            </div>
                          ) : (
                            msg.text
                          )}
                        </div>
                        <span className="urbania-time">{msg.time}</span>
                      </div>
                      {msg.type === 'bot' && msg.suggestions?.length > 0 && (
                        <div className="urbania-suggestions">
                          {msg.suggestions.map((s, i) => (
                            <button
                              key={i}
                              className={`urbania-chip urbania-chip-${s.action}`}
                              onClick={() => handleSuggestion(s)}
                            >
                              {s.text}
                              {s.action === 'navigate' && <span className="urbania-chip-arrow">→</span>}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                  {isLoading && (
                    <div className="urbania-msg urbania-msg-bot">
                      <div className="urbania-bubble urbania-bubble-loading">
                        <div className="urbania-typing"><span /><span /><span /></div>
                        <p className="urbania-loading-hint">Consultando datos del sistema...</p>
                      </div>
                      {loadingSuggestions.length > 0 && (
                        <div className="urbania-suggestions urbania-suggestions-loading">
                          <p className="urbania-loading-label">Mientras esperas, puedes ir a:</p>
                          {loadingSuggestions.map((s, i) => (
                            <button key={i} className="urbania-chip urbania-chip-navigate urbania-chip-secondary" onClick={() => handleSuggestion(s)}>
                              {s.text}<span className="urbania-chip-arrow">→</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Context warning */}
                {contextWarning && (
                  <div className={`urbania-context-warning ${contextWarning}`}>
                    {contextWarning === 'yellow' ? (
                      <span>⚠️ Estas llegando al limite de este chat ({messageCount} mensajes)</span>
                    ) : (
                      <>
                        <span>🔴 Limite de contexto alcanzado</span>
                        <button className="urbania-context-warning-btn" onClick={handleNewChat}>
                          + Nuevo chat
                        </button>
                      </>
                    )}
                  </div>
                )}

                {/* Input */}
                <div className="urbania-input-area">
                  <input
                    ref={inputRef}
                    type="text"
                    className="urbania-input"
                    placeholder="Pregunta sobre el sistema..."
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    disabled={isLoading}
                    maxLength={500}
                  />
                  <button
                    className="urbania-send-btn"
                    onClick={() => sendMessage(input)}
                    disabled={isLoading || !input.trim()}
                    title="Enviar"
                  >
                    {isLoading ? (
                      <span className="urbania-send-spinner" />
                    ) : (
                      <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                        <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
                      </svg>
                    )}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* ── Sidebar overlay (modo normal 380px) ── */}
              {showSidebar && (
                <UrbanIASidebar
                  conversations={conversations}
                  folders={folders}
                  activeConversationId={activeConversationId}
                  onSelectConversation={handleSelectConversation}
                  onNewChat={handleNewChat}
                  onDeleteConversation={handleDeleteConversation}
                  onRenameConversation={handleRenameConversation}
                  onCreateFolder={handleCreateFolder}
                  onDeleteFolder={handleDeleteFolder}
                  onMoveToFolder={handleMoveToFolder}
                  isOverlay={true}
                  onClose={() => setShowSidebar(false)}
                />
              )}

              {/* Mensajes (modo normal) */}
              <div className="urbania-messages">
                {messages.map(msg => (
                  <div key={msg.id} className={`urbania-msg urbania-msg-${msg.type}`}>
                    <div className="urbania-bubble">
                      <div className="urbania-bubble-text">
                        {msg.type === 'bot' ? (
                          <div className="urbania-markdown">
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.text}</ReactMarkdown>
                          </div>
                        ) : (
                          msg.text
                        )}
                      </div>
                      <span className="urbania-time">{msg.time}</span>
                    </div>
                    {msg.type === 'bot' && msg.suggestions?.length > 0 && (
                      <div className="urbania-suggestions">
                        {msg.suggestions.map((s, i) => (
                          <button
                            key={i}
                            className={`urbania-chip urbania-chip-${s.action}`}
                            onClick={() => handleSuggestion(s)}
                          >
                            {s.text}
                            {s.action === 'navigate' && <span className="urbania-chip-arrow">→</span>}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                {isLoading && (
                  <div className="urbania-msg urbania-msg-bot">
                    <div className="urbania-bubble urbania-bubble-loading">
                      <div className="urbania-typing"><span /><span /><span /></div>
                      <p className="urbania-loading-hint">Consultando datos del sistema...</p>
                    </div>
                    {loadingSuggestions.length > 0 && (
                      <div className="urbania-suggestions urbania-suggestions-loading">
                        <p className="urbania-loading-label">Mientras esperas, puedes ir a:</p>
                        {loadingSuggestions.map((s, i) => (
                          <button key={i} className="urbania-chip urbania-chip-navigate urbania-chip-secondary" onClick={() => handleSuggestion(s)}>
                            {s.text}<span className="urbania-chip-arrow">→</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Context warning (modo normal) */}
              {contextWarning && (
                <div className={`urbania-context-warning ${contextWarning}`}>
                  {contextWarning === 'yellow' ? (
                    <span>⚠️ Llegando al limite ({messageCount} msgs)</span>
                  ) : (
                    <>
                      <span>🔴 Limite alcanzado</span>
                      <button className="urbania-context-warning-btn" onClick={handleNewChat}>
                        + Nuevo
                      </button>
                    </>
                  )}
                </div>
              )}

              {/* Input (modo normal) */}
              <div className="urbania-input-area">
                <input
                  ref={inputRef}
                  type="text"
                  className="urbania-input"
                  placeholder="Pregunta sobre el sistema..."
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  disabled={isLoading}
                  maxLength={500}
                />
                <button
                  className="urbania-send-btn"
                  onClick={() => sendMessage(input)}
                  disabled={isLoading || !input.trim()}
                  title="Enviar"
                >
                  {isLoading ? (
                    <span className="urbania-send-spinner" />
                  ) : (
                    <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                      <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
                    </svg>
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── BOTÓN FLOTANTE ── */}
      <button
        className={`urbania-fab ${isOpen ? 'urbania-fab-active' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        title="UrbanIA - Asistente Inteligente"
      >
        {isOpen ? (
          <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
          </svg>
        ) : (
          <span className="urbania-fab-icon">🤖</span>
        )}
        <span className="urbania-fab-label">UrbanIA</span>
        {!isOpen && (
          <span className="urbania-fab-pulse" />
        )}
      </button>

      {/* ── MODAL DE NAVEGACIÓN ── */}
      {navModal && (
        <div className="urbania-nav-overlay" onClick={() => setNavModal(null)}>
          <div className="urbania-nav-modal" onClick={e => e.stopPropagation()}>
            <div className="urbania-nav-icon">🔗</div>
            <h3>¿Ir a esta sección?</h3>
            <p>Serás llevado a <strong>{navModal.text.replace(/[^\w\s]/gi, '').trim()}</strong></p>
            <p className="urbania-nav-note">El chat seguirá disponible cuando regreses.</p>
            <div className="urbania-nav-actions">
              <button
                className="urbania-nav-btn urbania-nav-btn-cancel"
                onClick={() => setNavModal(null)}
              >
                Seguir chateando
              </button>
              <button
                className="urbania-nav-btn urbania-nav-btn-confirm"
                onClick={confirmNavigation}
              >
                Ir ahora →
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default UrbanIA;